import React, {useContext, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {
    Button,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader, Select,
    TextField,
} from '../../../patterns';
import {formatToYearAndMonthAndDayWithDateInput, getLocaleFromLanguage} from '../base/dateUtils';
import {StoreContext} from '../base/root-store';
import Datepicker from '../../../patterns/components/forms/datepicker/datepicker';
import {DateRange} from 'react-day-picker';

export const EditCompanyContactsModal = observer((): JSX.Element => {
    const store = useContext(StoreContext).companyStore;
    const userStore = useContext(StoreContext).userStore;
    const initialCompanySettings = store.variables.companyInEditing;
    const companyId = store.payload!.companySettingsPayload.companyId;
    const timezoneOptions = store.payload!.companySettingsPayload.timezoneId;

    const [name, setName] = useState<string>(initialCompanySettings ? initialCompanySettings.name : '');
    const [address, setAddress] = useState<string>(initialCompanySettings ? initialCompanySettings.address : '');
    const [regNumber, setRegNumber] = useState<string>(initialCompanySettings ? initialCompanySettings.regNumber : '');
    const [surveyStartDate, setSurveyStartDate] = useState<Date | DateRange | undefined>(initialCompanySettings ? new Date(initialCompanySettings.surveyStartDate) : undefined);
    const [questionsTimezone, setQuestionsTimezone] = useState<string>(initialCompanySettings ? initialCompanySettings.questionsTimezone : 'Europe/Tallinn');

    return (
        <Modal size="small">
            <ModalHeader>
                <h4>{store.label('company.company-modal.title-contacts')}</h4>
            </ModalHeader>
            <ModalContent>
                <div className="text">
                    <TextField
                        label={store.label('company.company-modal.company-name')}
                        defaultValue={name}
                        onChange={(value): void => setName(value)}
                        id='company-modal.company-name'
                        name='company-modal.company-name'
                        description={store.variables.showValidation && store.getNameError(name, 'company_name_forbidden') !== undefined ? store.label(`common.validation.${store.getNameError(name, 'company_name_forbidden')}`) : undefined}
                        invalid={store.variables.showValidation && store.getNameError(name, 'company_name_forbidden') !== undefined}
                    />
                    <Datepicker
                        field={{
                            label: store.label('company.company-modal.start-date'),
                            focusLabel: store.label('company.company-modal.start-date'),
                            id: 'company-modal.start-date',
                            name: 'company-modal.start-date',
                            description: store.variables.showValidation && store.checkIfDateIsMissing(surveyStartDate as Date) !== undefined ? store.label(`common.validation.${store.checkIfDateIsMissing(surveyStartDate as Date)}`) : undefined,
                            invalid: store.variables.showValidation && store.checkIfDateIsMissing(surveyStartDate as Date) !== undefined,
                        }}
                        value={surveyStartDate as Date}
                        format={'dd.MM.yyyy'}
                        type={'single'}
                        onDateChange={(value: Date | DateRange | undefined): void => {
                            setSurveyStartDate(new Date(formatToYearAndMonthAndDayWithDateInput(value as Date)));
                        }}
                        locale={getLocaleFromLanguage(userStore.payload?.language)}
                    />
                    <TextField
                        label={store.label('company.company-modal.company-address')}
                        defaultValue={address}
                        onChange={(value): void => setAddress(value)}
                        id='company-modal.company-address'
                        name='company-modal.company-address'
                        description={store.variables.showValidation && store.getNameError(address, 'company_address_forbidden') !== undefined ? store.label(`common.validation.${store.getNameError(address, 'company_address_forbidden')}`) : undefined}
                        invalid={store.variables.showValidation && store.getNameError(address, 'company_address_forbidden') !== undefined}
                    />
                    <TextField
                        label={store.label('company.company-modal.reg-number')}
                        defaultValue={regNumber}
                        onChange={(value): void => setRegNumber(value)}
                        id='company-modal.reg-number'
                        name='company-modal.reg-number'
                        description={store.variables.showValidation && store.getNameError(regNumber, 'company_number_forbidden') !== undefined ? store.label(`common.validation.${store.getNameError(regNumber, 'company_number_forbidden')}`) : undefined}
                        invalid={store.variables.showValidation && store.getNameError(regNumber, 'company_number_forbidden') !== undefined}
                    />
                    <Select
                        label={store.label('company.company-modal.select-questions-timezone')}
                        defaultValue={questionsTimezone}
                        onChange={(value): void => setQuestionsTimezone('' + value)}
                        options={
                            ...timezoneOptions.map((timezone) => ({
                                label: timezone,
                                value: timezone,
                            }))}
                        id='company-select-timezone'
                        name='company-select-timezone'
                        type={undefined}
                    />
                </div>
            </ModalContent>
            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button block={true} color="white" text={store.label('company.modal.cancel')}/>
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button block={true} text={store.label('company.modal.save')}
                            onClick={(): void => store.saveCompanyContacts({
                                companyId,
                                name,
                                address,
                                regNumber,
                                surveyStartDate: formatToYearAndMonthAndDayWithDateInput(surveyStartDate as Date),
                                questionsTimezone,
                            })}/>
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
